import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  Copy,
  KeyRound,
  Settings as SettingsIcon,
  Terminal,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { useConfirm } from '../components/ui/ConfirmDialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { formatRelative } from '../lib/utils'

const HOOK_URL = 'https://squadron-mcp.gsolutions-it.com'

type McpToken = {
  id: string
  name: string
  tokenPrefix: string
  description: string | null
  scopes: string[] | null
  createdAt: string
  lastUsedAt: string | null
  lastUsedIp: string | null
  expiresAt: string | null
}

type ScopePreset = {
  id: 'full' | 'assets'
  label: string
  description: string
  scopes: string[]
}

const SCOPE_PRESETS: ScopePreset[] = [
  {
    id: 'full',
    label: 'Pieno accesso (Claude Desktop / MCP)',
    description:
      'Equivalente al tuo account: leggi/scrivi tutti i dati del tenant. Usalo per la connessione MCP di Claude Desktop o per script fidati.',
    scopes: ['*'],
  },
  {
    id: 'assets',
    label: 'Solo upload asset',
    description:
      'Può solo caricare e leggere asset (proposal mockup, branding) sul tuo tenant. Non può vedere clienti, fatture, proposte, time tracking. Ideale per script di import o CI.',
    scopes: ['assets:read', 'assets:write'],
  },
]

function buildSettingsSnippet(token: string) {
  const t = token || '<INCOLLA_QUI_IL_TUO_TOKEN>'
  return `{
  "hooks": {
    "UserPromptSubmit": [
      {
        "hooks": [
          {
            "type": "command",
            "command": "curl -fsS -X POST ${HOOK_URL}/v1/hooks/prompt-submit -H 'Authorization: Bearer ${t}' -H 'Content-Type: application/json' --data-binary @- | node -e \\"const r=JSON.parse(require('fs').readFileSync(0,'utf8'));process.stderr.write(r.header||'');process.stdout.write(r.header||'')\\"",
            "timeout": 5
          }
        ]
      }
    ],
    "PostToolUse": [
      {
        "hooks": [
          {
            "type": "command",
            "command": "curl -fsS -X POST ${HOOK_URL}/v1/hooks/tool-use -H 'Authorization: Bearer ${t}' -H 'Content-Type: application/json' --data-binary @- > /dev/null",
            "timeout": 5
          }
        ]
      }
    ],
    "Stop": [
      {
        "hooks": [
          {
            "type": "command",
            "command": "curl -fsS -X POST ${HOOK_URL}/v1/hooks/stop -H 'Authorization: Bearer ${t}' -H 'Content-Type: application/json' --data-binary @- > /dev/null",
            "timeout": 5
          }
        ]
      }
    ]
  }
}`
}

function CopyButton({ text, label = 'Copia' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  async function onClick() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium bg-zinc-800 hover:bg-zinc-700 text-zinc-200 transition"
    >
      {copied ? <Check className="size-3.5 text-emerald-400" /> : <Copy className="size-3.5" />}
      {copied ? 'Copiato' : label}
    </button>
  )
}

export function SetupPage() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [token, setToken] = useState('')
  const [newTokenName, setNewTokenName] = useState('')
  const [presetId, setPresetId] = useState<ScopePreset['id']>('full')
  const [newlyCreated, setNewlyCreated] = useState<{
    plain: string
    name: string
    scopes: string[]
  } | null>(null)

  const tokensQuery = useQuery({
    queryKey: ['mcp-tokens'],
    queryFn: () => apiClient.get<{ results: McpToken[]; total: number }>('/auth/mcp-tokens'),
  })

  const createToken = useMutation({
    mutationFn: (args: { name: string; scopes: string[] }) =>
      apiClient.post<{
        id: string
        tokenPrefix: string
        name: string
        scopes: string[]
        plain: string
      }>('/auth/mcp-tokens', { name: args.name, scopes: args.scopes }),
    onSuccess: (r) => {
      setNewlyCreated({ plain: r.plain, name: r.name, scopes: r.scopes ?? ['*'] })
      setToken(r.plain)
      setNewTokenName('')
      qc.invalidateQueries({ queryKey: ['mcp-tokens'] })
    },
  })

  const revokeToken = useMutation({
    mutationFn: (id: string) => apiClient.del(`/auth/mcp-tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-tokens'] }),
  })

  const snippet = useMemo(() => buildSettingsSnippet(token), [token])

  return (
    <AppLayout>
      <PageHeader
        title="Setup Claude Code"
        description="Token MCP e configurazione hook server-side"
      />

      <div className="space-y-6">
        {/* Step 1 — Token management */}
        <Card>
          <CardBody>
            <div className="flex items-start gap-3 mb-3">
              <div className="size-7 rounded-full bg-cyan-500/15 text-cyan-300 grid place-items-center text-sm font-bold">
                1
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-zinc-50 flex items-center gap-2">
                  <KeyRound className="size-5" /> Token MCP
                </h2>
                <p className="text-sm text-zinc-400">
                  Genera un nuovo token o usa uno esistente. Il token compare in chiaro{' '}
                  <strong>solo al momento della creazione</strong> — copialo subito.
                </p>
              </div>
            </div>

            {/* New token banner */}
            {newlyCreated && (
              <div className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-emerald-200 mb-1">
                      <Check className="size-4" />
                      <span className="text-sm font-semibold">
                        Token "{newlyCreated.name}" creato
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {newlyCreated.scopes.map((s) => (
                        <Badge key={s} variant={s === '*' ? 'warning' : 'default'}>
                          {s === '*' ? 'pieno accesso' : s}
                        </Badge>
                      ))}
                    </div>
                    <code className="block text-xs font-mono text-emerald-100 break-all bg-zinc-950/70 rounded px-2 py-1.5 mt-2">
                      {newlyCreated.plain}
                    </code>
                    <div className="flex items-center gap-2 text-xs text-amber-300 mt-2">
                      <AlertTriangle className="size-3.5 shrink-0" />
                      Copialo ora — non sarà più visibile in chiaro.
                    </div>
                  </div>
                  <div className="shrink-0">
                    <CopyButton text={newlyCreated.plain} />
                  </div>
                </div>
              </div>
            )}

            {/* Create new */}
            <div className="space-y-3 mb-4">
              <div>
                <Label htmlFor="newTokenName">Nome nuovo token</Label>
                <Input
                  id="newTokenName"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.currentTarget.value)}
                  placeholder="Es. Laptop personale"
                />
              </div>

              <div>
                <Label>Permessi (scope)</Label>
                <div className="grid gap-2">
                  {SCOPE_PRESETS.map((p) => {
                    const active = presetId === p.id
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPresetId(p.id)}
                        className={`text-left rounded-md border px-3 py-2.5 transition ${
                          active
                            ? 'border-cyan-500/60 bg-cyan-500/10'
                            : 'border-zinc-800 bg-zinc-950/40 hover:border-zinc-700'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className={`size-3.5 rounded-full border flex items-center justify-center ${
                              active ? 'border-cyan-400 bg-cyan-400/30' : 'border-zinc-600'
                            }`}
                          >
                            {active && <div className="size-1.5 rounded-full bg-cyan-300" />}
                          </div>
                          <span className="text-sm font-medium text-zinc-100">{p.label}</span>
                          <div className="flex gap-1 ml-1">
                            {p.scopes.map((s) => (
                              <Badge key={s} variant={s === '*' ? 'warning' : 'default'}>
                                {s === '*' ? 'pieno accesso' : s}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-zinc-400 pl-5">{p.description}</p>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    const preset = SCOPE_PRESETS.find((p) => p.id === presetId) ?? SCOPE_PRESETS[0]
                    if (!preset) return
                    createToken.mutate({ name: newTokenName, scopes: preset.scopes })
                  }}
                  disabled={createToken.isPending}
                >
                  <KeyRound className="size-4" />{' '}
                  {createToken.isPending ? 'Generazione…' : 'Genera token'}
                </Button>
              </div>
            </div>

            {/* Existing tokens list */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Token attivi</h3>
              {tokensQuery.isLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : tokensQuery.data && tokensQuery.data.results.length > 0 ? (
                <ul className="space-y-1.5">
                  {tokensQuery.data.results.map((t) => (
                    <li
                      key={t.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-zinc-100">{t.name}</span>
                          {(t.scopes ?? ['*']).map((s) => (
                            <Badge key={s} variant={s === '*' ? 'warning' : 'default'}>
                              {s === '*' ? 'pieno accesso' : s}
                            </Badge>
                          ))}
                        </div>
                        <div className="text-xs text-zinc-500 flex flex-wrap gap-x-3 mt-0.5">
                          <code className="font-mono">{t.tokenPrefix}…</code>
                          <span>creato {formatRelative(t.createdAt)}</span>
                          {t.lastUsedAt && <span>usato {formatRelative(t.lastUsedAt)}</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await confirm({
                            title: `Revocare il token "${t.name}"?`,
                            message:
                              'Operazione irreversibile. Tutte le richieste autenticate con questo token smetteranno di funzionare immediatamente — verifica che nessuna integrazione attiva lo stia usando.',
                            confirmLabel: 'Revoca',
                            tone: 'danger',
                          })
                          if (ok) revokeToken.mutate(t.id)
                        }}
                        disabled={revokeToken.isPending}
                        className="p-1.5 rounded hover:bg-red-900/30 text-zinc-500 hover:text-red-400 disabled:opacity-30 transition"
                        aria-label="Revoca token"
                        title="Revoca"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty
                  icon={KeyRound}
                  title="Nessun token attivo"
                  description="Crea un nuovo token sopra per iniziare."
                />
              )}
            </div>

            {/* Use existing token */}
            <div className="mt-5 pt-4 border-t border-zinc-800/60">
              <Label htmlFor="token">Oppure incolla un token che hai già</Label>
              <Input
                id="token"
                value={token}
                onChange={(e) => setToken(e.currentTarget.value)}
                placeholder="skh_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="font-mono text-sm"
              />
              {token && !token.startsWith('skh_') && (
                <p className="mt-1 text-xs text-amber-400">
                  I token Squadron iniziano con <code>skh_</code>.
                </p>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Step 2 — settings.json */}
        <Card>
          <CardBody>
            <div className="flex items-start gap-3 mb-3">
              <div className="size-7 rounded-full bg-cyan-500/15 text-cyan-300 grid place-items-center text-sm font-bold">
                2
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-50 flex items-center gap-2">
                  <SettingsIcon className="size-5" /> ~/.claude/settings.json
                </h2>
                <p className="text-sm text-zinc-400">
                  Incolla nella sezione <code>hooks</code> di <code>settings.json</code> globale. Se
                  hai già hook configurati, unisci manualmente.
                </p>
              </div>
            </div>
            <div className="relative">
              <pre className="bg-zinc-950/80 border border-zinc-800 rounded-md p-4 overflow-x-auto text-xs leading-relaxed text-zinc-200">
                {snippet}
              </pre>
              <div className="absolute top-2 right-2">
                <CopyButton text={snippet} label="Copia settings.json" />
              </div>
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              Path: Windows <code>C:\Users\&lt;utente&gt;\.claude\settings.json</code> · Linux/Mac{' '}
              <code>~/.claude/settings.json</code>
            </p>
          </CardBody>
        </Card>

        {/* Step 3 — Test */}
        <Card>
          <CardBody>
            <div className="flex items-start gap-3 mb-3">
              <div className="size-7 rounded-full bg-cyan-500/15 text-cyan-300 grid place-items-center text-sm font-bold">
                3
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-50 flex items-center gap-2">
                  <Terminal className="size-5" /> Test rapido
                </h2>
                <p className="text-sm text-zinc-400">
                  Verifica che il token sia valido senza riavviare Claude Code:
                </p>
              </div>
            </div>
            <pre className="bg-zinc-950/80 border border-zinc-800 rounded-md p-4 overflow-x-auto text-xs leading-relaxed text-zinc-200">
              {`curl -sS -X POST ${HOOK_URL}/v1/hooks/prompt-submit \\
  -H "Authorization: Bearer ${token || '<TOKEN>'}" \\
  -H "Content-Type: application/json" \\
  -d '{"session_id":"550e8400-e29b-41d4-a716-446655440000","prompt":"test"}'`}
            </pre>
            <p className="mt-3 text-xs text-zinc-500">
              Dovresti vedere JSON con <code>header</code>, <code>taskId</code>, <code>level</code>,{' '}
              <code>confidence</code>. Se <code>Unauthorized</code> il token è sbagliato o revocato.
            </p>
          </CardBody>
        </Card>

        {/* Cutover info */}
        <Card>
          <CardBody>
            <div className="flex items-start gap-3">
              <Badge variant="warning">Cutover</Badge>
              <div className="space-y-2 text-sm text-zinc-300">
                <p>
                  Se i tuoi hook puntano ancora al vecchio sistema (Python+SQLite locale),
                  sovrascrivendo <code>settings.json</code> con questa configurazione il vecchio
                  smette di tracciare. Il nuovo prende il sopravvento dal prossimo prompt.
                </p>
                <p className="text-zinc-400">
                  Matching L1/L2/L4 gira ora server-side. Per dare keyword a un task, edita{' '}
                  <code>keywords</code> via <code>agile.upsertTask</code> o usa la riattribuzione
                  con "Impara keyword" da <code>/time</code> tab Live.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  )
}
